<template>
  <div
    v-if="facilities.length || (showIconFacilities && filteredTrimmedHighlightedFacilities.length)"
    class="container mx-auto mt-4 lg:mt-12"
    :class="{ 'no-max-width': maxWidth }"
  >
    <div class="flex flex-wrap -mx-4">
      <div class="w-12/12 px-4">
        <ScreenWidthProvider v-slot="{ isNarrower }">
          <h2
            class="font-secondary"
            :data-i18n="title"
          >
            {{ $t(title) }}
          </h2>
          <template v-if="showIconFacilities">
            <div
              v-for="(facility, index) in filteredTrimmedHighlightedFacilities"
              :key="`f${index}`"
              class="inline-block mr-6"
            >
              <fa :icon="getIcon(facility)" />
              <span class="ml-2 font-bold">
                {{ $t(`hotelFacility.${facility}`) }}
              </span>
            </div>
          </template>
          <div
            class="mt-6 facilities-grid-container"
            :class="{ 'facilities-grid-container--collapsed': isCollapsed && isNarrower('md') && !isExpanded }"
          >
            <ul class="facilities-grid">
              <li
                v-for="(facility, index) in facilities"
                :key="`f_${index}`"
              >
                <span>
                  {{ facility.text }}
                </span>
                <span
                  v-if="facility.timeFrom"
                  class="ml-2"
                >{{ facilityTime(facility.timeFrom) }}<span
                  v-if="facility.timeTo && facility.timeTo !== facility.timeFrom"
                  class="ml-2"
                >- {{ facilityTime(facility.timeTo) }}</span>
                </span>
                <span
                  v-else-if="facility.timeTo"
                  class="ml-2"
                >{{ facilityTime(facility.timeTo) }}</span>
                <span
                  v-if="facility.indFee"
                  class="facility-fee-hotel ml-2 font-bold"
                >{{ $t('hotelFacilityFee') }}</span>
              </li>
            </ul>
            <!-- list subFacilitys if available-->
            <ul
              v-if="subFacilitys"
              class="mt-4 facilities-grid"
            >
              <strong>
                {{ subFacilitys.name }}
              </strong>
              <li
                v-for="(subFacility, subIndex) in subFacilitys.facilities"
                :key="`sf_${subIndex}`"
              >
                <span>
                  {{ subFacility.text }}
                </span>
                <span
                  v-if="subFacility.timeFrom"
                  class="ml-2"
                >{{ facilityTime(subFacility.timeFrom) }}<span
                  v-if="subFacility.timeTo && subFacility.timeTo !== subFacility.timeFrom"
                  class="ml-2"
                >- {{ facilityTime(subFacility.timeTo) }}</span>
                </span>
                <span
                  v-else-if="subFacility.timeTo"
                  class="ml-2"
                >{{ facilityTime(subFacility.timeTo) }}</span>
                <span
                  v-if="subFacility.indFee"
                  class="facility-fee-hotel ml-2 font-bold"
                >{{ $t('hotelFacilityFee') }}</span>
              </li>
            </ul>
          </div>
          <button
            v-if="isNarrower('md') && !isExpanded"
            class="btn-show-more mt-2 cursor-pointer btn-rounded"
            @click="showMoreFacilities"
          >
            <span class="font-bold">
              {{ isCollapsed ? $t('charterHotelShowMore') : $t('charterHotelShowLess') }}
            </span>
            <fa
              class="fa-chevron-down ml-2"
              icon="fa-chevron-down"
              :class="{ 'rotate-180': !isCollapsed }"
            />
          </button>
          <template v-if="showHotelMapBox">
            <HotelMapBoxViewer
              :hotel-info="hotelInfo"
              see-full-map-button-position="top"
            />
          </template>
        </ScreenWidthProvider>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()

const props = defineProps({
  title: {
    type: String,
    default: '',
  },

  hotelInfo: {
    type: Object,
    default: () => ({}),
  },

  maxWidth: {
    type: Boolean,
    default: false,
  },

  showIconFacilities: {
    type: Boolean,
    default: false,
  },

  isExpanded: {
    type: Boolean,
    default: false,
  },

  showHotelMapBox: {
    type: Boolean,
    default: false,
  },
})

const subFacilitys = ref(null)
const isCollapsed = ref(true)
const { getIcon } = useCharterHotelMixin()

const facilities = computed(() => {
  if (!props.hotelInfo.meta || Object.keys(props.hotelInfo.meta.Facilities_grouped || {}).length === 0) {
    return []
  }

  const groupedFacilities = Object.keys(props.hotelInfo.meta.Facilities_grouped || {})
    .filter(k => k.startsWith(7) && props.hotelInfo.meta.Facilities_grouped[k])
    .reduce((acc, key) => {
      const facilities = props.hotelInfo.meta.Facilities_grouped[key].facilities.filter(facility => {
        return facility.indYesOrNo !== false
      })
      acc.push(...facilities)
      return acc
    }, [])

  if (props.hotelInfo.meta.Facilities_grouped[30]) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    subFacilitys.value = {
      name: t('hotelAcceptedPayments'),
      facilities: props.hotelInfo.meta.Facilities_grouped[30].facilities,
    }
  }

  return groupedFacilities
})

const filteredTrimmedHighlightedFacilities = computed(() => {
  return (props.hotelInfo?.highlighted_facilities || [])
    .filter(Boolean)
    .map(facility => facility.trim())
})

const facilityTime = (time) => {
  return time.match(/^\d+:\d+/)[0]
}

const showMoreFacilities = () => {
  isCollapsed.value = !isCollapsed.value
}
</script>

<style lang="scss" scoped>
.facilities-grid-container{
  max-height: 100%;
  overflow: hidden;
  transition: max-height 0.3s ease;

  &--collapsed {
    max-height: 200px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50px;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    }
  }
}
.facilities-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.3rem 0;
  position: relative;
}

.facilities-grid li {
  max-width: calc(100% - 40px);
  word-wrap: break-word;
}

.btn-show-more {
  padding: 0.5rem 1rem;
  border-radius: 22px;
  background-color: theme('colors.light-turquoise');
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 120px;

  .fa-chevron-down {
    transition: transform 0.3s ease;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }
}

@media (min-width: theme('screens.sm')) {
  .facilities-grid {
    grid-template-columns: repeat(2, 1fr);
    max-height: none;
    overflow: visible;

    &--collapsed {
      max-height: none;

      &::after {
        display: none;
      }
    }
  }

  .facilities-grid::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(50% - 40px - 0.5px);
    width: 1px;
    background-color: #ccc;
  }
}

@media (min-width: theme('screens.md')) {
  .facilities-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .facilities-grid::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(33.33% - 3px);
    width: 1px;
    background-color: #ccc;
  }

  .facilities-grid::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(66.66% - 3px);
    width: 1px;
    background-color: #ccc;
  }
}

@media (min-width: theme('screens.lg')) {
  .facilities-grid::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(33.33% - 40px - 0.5px);
    width: 1px;
    background-color: #ccc;
  }

  .facilities-grid::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(66.66% - 40px - 0.5px);
    width: 1px;
    background-color: #ccc;
  }
}

.facility-fee-hotel {
  font-weight: bold;
}
.no-max-width {
  max-width: none !important;
}
</style>